import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  FormControlLabel,
} from '@mui/material';
import {
  Edit3,
  RefreshCw,
  Download,
  Columns,
  Search,
  Building2,
} from 'lucide-react';
import { useAuth } from '../../../../AuthProvider';
import { CircularProgress, Pagination } from '@mui/material';
import EditUnitDialog from './EditUnitDialog';

const UnitsGrid = ({ initialBuildingFilter, onFilterApplied }) => {
  const { getAccessToken } = useAuth();
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterBuilding, setFilterBuilding] = useState(
    () => initialBuildingFilter || 'all',
  );
  const [filterBed, setFilterBed] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const [units, setUnits] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [editingUnit, setEditingUnit] = useState(null);

  const [visibleColumns, setVisibleColumns] = useState({
    building: true,
    unit: true,
    bed: true,
    bath: true,
    sqft: true,
    rentPerSqft: true, // $/SF
    floor: true,
    condition: false,
    amenities: false,
    currentRent: true,
    marketRent: true,
    delta: true,
    status: true,
    leaseEnd: true,
    lastUpdate: false,
    notes: false,
  });

  // Fetch units data
  const fetchUnits = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const accessToken = getAccessToken();
      const url = new URL(`${process.env.REACT_APP_NODE_URL}/buildings/units`);
      url.searchParams.append('page', page.toString());
      url.searchParams.append('limit', limit.toString());
      if (searchQuery) {
        url.searchParams.append('search', searchQuery);
      }
      if (filterBuilding !== 'all') {
        url.searchParams.append('filterBuilding', filterBuilding);
      }
      if (filterBed !== 'all') {
        url.searchParams.append('filterBed', filterBed);
      }
      if (filterStatus !== 'all') {
        url.searchParams.append('filterStatus', filterStatus);
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage =
          data.message || `HTTP error! status: ${response.status}`;
        if (response.status === 404) {
          setError('noUnits');
          setUnits([]);
          setBuildings(data.buildings || []);
          setTotal(0);
          setTotalPages(0);
          return;
        }
        throw new Error(errorMessage);
      }

      if (data.status) {
        setUnits(data.data || []);
        setBuildings(data.buildings || []);
        setTotal(data.pagination?.total || 0);
        setTotalPages(data.pagination?.totalPages || 0);
      } else {
        const errorMessage = data.message || 'Failed to fetch units';
        if (
          errorMessage.includes('No units found') ||
          errorMessage.includes('no units')
        ) {
          setError('noUnits');
          setUnits([]);
          setBuildings(data.buildings || []);
          setTotal(0);
          setTotalPages(0);
          return;
        }
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('Error fetching units:', err);
      if (err.message && err.message.includes('No units found')) {
        setError('noUnits');
      } else {
        setError(err.message || 'Failed to fetch units');
      }
      setUnits([]);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    limit,
    searchQuery,
    filterBuilding,
    filterBed,
    filterStatus,
    getAccessToken,
  ]);

  // Handle initial building filter from parent (e.g. when user clicks building name in Buildings table)
  useEffect(() => {
    if (initialBuildingFilter) {
      setFilterBuilding(initialBuildingFilter);
      setPage(1); // Reset to first page
      // Don't call onFilterApplied here - it clears parent state too early and breaks
      // filter persistence on React Strict Mode remount. We clear when user selects "All Buildings".
    }
  }, [initialBuildingFilter]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1); // Reset to first page on search/filter change
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, filterBuilding, filterBed, filterStatus]);

  // Fetch on mount and when filters/page change
  useEffect(() => {
    fetchUnits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchQuery, filterBuilding, filterBed, filterStatus]);

  const handlePageChange = (event, value) => {
    setPage(value);
    setSelectedIds([]); // Clear selection on page change
  };

  const handleSelectAll = () => {
    if (selectedIds.length === units.length && units.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(units.map(u => u.id));
    }
  };

  const handleSelect = id => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id],
    );
  };

  const handleOpenEditDialog = unit => {
    setEditingUnit(unit);
  };

  const handleDialogClose = () => {
    setEditingUnit(null);
  };

  const handleDialogSuccess = () => {
    fetchUnits();
    handleDialogClose();
  };

  const getStatusColor = status => {
    switch (status) {
      case 'Vacant':
        return { bg: '#FEE2E2', text: '#991B1B' };
      case 'Occupied':
        return { bg: '#E8F0F1', text: '#39585C' };
      case 'Notice':
        return { bg: '#FEF3C7', text: '#92400E' };
      default:
        return { bg: '#F3F4F6', text: '#374151' };
    }
  };

  // No need for client-side filtering since we're doing server-side filtering
  const filteredUnits = units;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: '#F9F9F9',
      }}
    >
      <Box
        sx={{
          borderBottom: '1px solid #E5E7EB',
          backgroundColor: '#FFFFFF',
          paddingX: { xs: '1rem', md: '1.5rem' },
          paddingY: '1rem',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              flex: 1,
              width: { xs: '100%', sm: 'auto' },
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
            }}
          >
            <TextField
              placeholder='Search units...'
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              size='small'
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    {loading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Search size={16} color='#6B7280' />
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                maxWidth: '448px',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#FFFFFF',
                },
              }}
            />

            <FormControl
              size='small'
              sx={{
                minWidth: { xs: '100%', sm: '180px' },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <Select
                value={filterBuilding}
                onChange={e => {
                  const value = e.target.value;
                  setFilterBuilding(value);
                  setPage(1);
                  if (value === 'all' && onFilterApplied) {
                    onFilterApplied();
                  }
                }}
              >
                <MenuItem value='all'>All Buildings</MenuItem>
                {buildings.map(building => (
                  <MenuItem key={building} value={building}>
                    {building}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              size='small'
              sx={{
                minWidth: { xs: '100%', sm: '140px' },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <Select
                value={filterBed}
                onChange={e => {
                  setFilterBed(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value='all'>All Beds</MenuItem>
                <MenuItem value='0'>Studio</MenuItem>
                <MenuItem value='1'>1 BR</MenuItem>
                <MenuItem value='2'>2 BR</MenuItem>
                <MenuItem value='3'>3 BR</MenuItem>
                <MenuItem value='4'>4 BR</MenuItem>
                <MenuItem value='5'>5 BR</MenuItem>
              </Select>
            </FormControl>

            <FormControl
              size='small'
              sx={{
                minWidth: { xs: '100%', sm: '140px' },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <Select
                value={filterStatus}
                onChange={e => {
                  setFilterStatus(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value='all'>All Status</MenuItem>
                <MenuItem value='Vacant'>Vacant</MenuItem>
                <MenuItem value='Occupied'>Occupied</MenuItem>
                <MenuItem value='Notice'>Notice</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            }}
          >
            <Button
              variant='outlined'
              size='small'
              startIcon={<Columns size={16} />}
              onClick={e => setColumnsMenuAnchor(e.currentTarget)}
              sx={{
                textTransform: 'none',
                borderColor: '#D1D5DB',
                color: '#374151',
                fontSize: { xs: '0.75rem', md: '0.875rem' },
              }}
            >
              Columns
            </Button>
            <Menu
              anchorEl={columnsMenuAnchor}
              open={Boolean(columnsMenuAnchor)}
              onClose={() => setColumnsMenuAnchor(null)}
            >
              {Object.entries(visibleColumns).map(([key, value]) => (
                <MenuItem key={key} onClick={() => {}}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={value}
                        onChange={e =>
                          setVisibleColumns(prev => ({
                            ...prev,
                            [key]: e.target.checked,
                          }))
                        }
                        size='small'
                      />
                    }
                    label={
                      key === 'rentPerSqft'
                        ? 'Rent Per Sq Ft ($/SF)'
                        : key.charAt(0).toUpperCase() +
                          key.slice(1).replace(/([A-Z])/g, ' $1')
                    }
                    sx={{ margin: 0 }}
                  />
                </MenuItem>
              ))}
            </Menu>
            <Button
              variant='outlined'
              size='small'
              startIcon={<Download size={16} />}
              sx={{
                textTransform: 'none',
                borderColor: '#D1D5DB',
                color: '#374151',
                fontSize: '0.875rem',
              }}
            >
              Export
            </Button>
          </Box>
        </Box>
      </Box>

      {selectedIds.length > 0 && (
        <Box
          sx={{
            borderBottom: '1px solid #E5E7EB',
            backgroundColor: '#E8F0F1',
            paddingX: { xs: '1rem', md: '1.5rem' },
            paddingY: '0.75rem',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: '0.75rem', sm: 0 },
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                fontWeight: 500,
                color: '#111827',
              }}
            >
              {selectedIds.length} units selected
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                flexWrap: 'wrap',
                width: { xs: '100%', sm: 'auto' },
                justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              }}
            >
              <Button
                size='small'
                variant='outlined'
                sx={{
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  borderColor: '#D1D5DB',
                  color: '#374151',
                }}
              >
                Bulk Set Rent
              </Button>
              <Button
                size='small'
                variant='outlined'
                sx={{
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  borderColor: '#D1D5DB',
                  color: '#374151',
                }}
              >
                Update Status
              </Button>
              <Button
                size='small'
                variant='outlined'
                startIcon={<RefreshCw size={16} />}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  borderColor: '#D1D5DB',
                  color: '#374151',
                }}
              >
                Recalculate
              </Button>
              <Button
                size='small'
                variant='outlined'
                sx={{
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  borderColor: '#D1D5DB',
                  color: '#374151',
                }}
              >
                Export Selected
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading && units.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
            }}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
              flexDirection: 'column',
              gap: '1rem',
              padding: '2rem',
            }}
          >
            {error === 'noUnits' ? (
              <>
                <Building2 size={48} color='#6B7280' />
                <Typography
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: '#111827',
                    textAlign: 'center',
                  }}
                >
                  No Units Found
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                    textAlign: 'center',
                    maxWidth: '400px',
                  }}
                >
                  {searchQuery ||
                  filterBuilding !== 'all' ||
                  filterBed !== 'all' ||
                  filterStatus !== 'all'
                    ? 'No units match your filters. Try adjusting your search or filters.'
                    : "You haven't added any units yet. Add units to your buildings to get started."}
                </Typography>
              </>
            ) : (
              <>
                <Typography color='error'>{error}</Typography>
                <Button variant='outlined' onClick={fetchUnits}>
                  Retry
                </Button>
              </>
            )}
          </Box>
        ) : units.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
              flexDirection: 'column',
              gap: '1rem',
              padding: '2rem',
            }}
          >
            <Building2 size={48} color='#6B7280' />
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 600,
                color: '#111827',
                textAlign: 'center',
              }}
            >
              No Units Found
            </Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: '#6B7280',
                textAlign: 'center',
                maxWidth: '400px',
              }}
            >
              {searchQuery ||
              filterBuilding !== 'all' ||
              filterBed !== 'all' ||
              filterStatus !== 'all'
                ? 'No units match your filters. Try adjusting your search or filters.'
                : "You haven't added any units yet. Add units to your buildings to get started."}
            </Typography>
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: 'none',
              backgroundColor: 'transparent',
              overflowX: 'auto',
            }}
          >
            <Table stickyHeader sx={{ minWidth: { xs: '1000px', md: 'auto' } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#F3F4F6' }}>
                  <TableCell padding='checkbox'>
                    <Checkbox
                      checked={
                        units.length > 0 && selectedIds.length === units.length
                      }
                      indeterminate={
                        selectedIds.length > 0 &&
                        selectedIds.length < units.length
                      }
                      onChange={handleSelectAll}
                      size='small'
                    />
                  </TableCell>
                  {visibleColumns.building && (
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        color: '#111827',
                        fontSize: '0.875rem',
                      }}
                    >
                      Building
                    </TableCell>
                  )}
                  {visibleColumns.unit && (
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        color: '#111827',
                        fontSize: '0.875rem',
                      }}
                    >
                      Unit
                    </TableCell>
                  )}
                  {visibleColumns.bed && (
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        color: '#111827',
                        fontSize: '0.875rem',
                      }}
                    >
                      Bed
                    </TableCell>
                  )}
                  {visibleColumns.bath && (
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        color: '#111827',
                        fontSize: '0.875rem',
                      }}
                    >
                      Bath
                    </TableCell>
                  )}
                  {visibleColumns.sqft && (
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        color: '#111827',
                        fontSize: '0.875rem',
                      }}
                    >
                      Sq Ft
                    </TableCell>
                  )}
                  {visibleColumns.rentPerSqft && (
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        color: '#111827',
                        fontSize: '0.875rem',
                      }}
                    >
                      Rent Per Sq Ft ($/SF)
                    </TableCell>
                  )}
                  {visibleColumns.floor && (
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        color: '#111827',
                        fontSize: '0.875rem',
                      }}
                    >
                      Floor
                    </TableCell>
                  )}
                  {visibleColumns.condition && (
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        color: '#111827',
                        fontSize: '0.875rem',
                      }}
                    >
                      Condition
                    </TableCell>
                  )}
                  {visibleColumns.amenities && (
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        color: '#111827',
                        fontSize: '0.875rem',
                      }}
                    >
                      Amenities
                    </TableCell>
                  )}
                  {visibleColumns.currentRent && (
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        color: '#111827',
                        fontSize: '0.875rem',
                      }}
                    >
                      Current Rent
                    </TableCell>
                  )}
                  {visibleColumns.marketRent && (
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        color: '#111827',
                        fontSize: '0.875rem',
                      }}
                    >
                      Market Rent
                    </TableCell>
                  )}
                  {visibleColumns.delta && (
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        color: '#111827',
                        fontSize: '0.875rem',
                      }}
                    >
                      Delta
                    </TableCell>
                  )}
                  {visibleColumns.status && (
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        color: '#111827',
                        fontSize: '0.875rem',
                      }}
                    >
                      Status
                    </TableCell>
                  )}
                  {visibleColumns.leaseEnd && (
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        color: '#111827',
                        fontSize: '0.875rem',
                      }}
                    >
                      Lease End
                    </TableCell>
                  )}
                  {visibleColumns.lastUpdate && (
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        color: '#111827',
                        fontSize: '0.875rem',
                      }}
                    >
                      Last Update
                    </TableCell>
                  )}
                  {visibleColumns.notes && (
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        color: '#111827',
                        fontSize: '0.875rem',
                      }}
                    >
                      Notes
                    </TableCell>
                  )}
                  <TableCell
                    sx={{
                      fontWeight: 500,
                      color: '#111827',
                      fontSize: '0.875rem',
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUnits.map(unit => {
                  const statusColors = getStatusColor(unit.status);
                  return (
                    <TableRow
                      key={unit.id}
                      sx={{
                        '&:hover': { backgroundColor: '#F9FAFB' },
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <TableCell padding='checkbox'>
                        <Checkbox
                          checked={selectedIds.includes(unit.id)}
                          onChange={() => handleSelect(unit.id)}
                          size='small'
                        />
                      </TableCell>
                      {visibleColumns.building && (
                        <TableCell
                          sx={{
                            fontWeight: 500,
                            color: '#111827',
                            fontSize: '0.875rem',
                          }}
                        >
                          {unit.building}
                        </TableCell>
                      )}
                      {visibleColumns.unit && (
                        <TableCell
                          sx={{ color: '#111827', fontSize: '0.875rem' }}
                        >
                          <Typography
                            sx={{ color: '#111827', fontSize: '0.875rem' }}
                          >
                            {unit.unit}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleColumns.bed && (
                        <TableCell
                          sx={{ color: '#6B7280', fontSize: '0.875rem' }}
                        >
                          <Typography
                            sx={{ color: '#6B7280', fontSize: '0.875rem' }}
                          >
                            {unit.bed}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleColumns.bath && (
                        <TableCell
                          sx={{ color: '#6B7280', fontSize: '0.875rem' }}
                        >
                          <Typography
                            sx={{ color: '#6B7280', fontSize: '0.875rem' }}
                          >
                            {unit.bath}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleColumns.sqft && (
                        <TableCell>
                          {unit.sqft ? (
                            <Typography
                              sx={{ color: '#6B7280', fontSize: '0.875rem' }}
                            >
                              {unit.sqft.toLocaleString()}
                            </Typography>
                          ) : (
                            <Typography
                              sx={{ color: '#6B7280', fontSize: '0.875rem' }}
                            >
                              -
                            </Typography>
                          )}
                        </TableCell>
                      )}
                      {visibleColumns.rentPerSqft && (
                        <TableCell>
                          {unit.sqft && unit.sqft > 0 ? (
                            <>
                              <Typography
                                sx={{ color: '#6B7280', fontSize: '0.875rem' }}
                              >
                                {unit.currentRent > 0
                                  ? `$${(unit.currentRent / unit.sqft).toFixed(
                                      2,
                                    )}`
                                  : '-'}
                              </Typography>
                              <Typography
                                sx={{ color: '#A0AEC0', fontSize: '0.75rem' }}
                              >
                                {unit.marketRent > 0
                                  ? `Mkt: $${(
                                      unit.marketRent / unit.sqft
                                    ).toFixed(2)}`
                                  : ''}
                              </Typography>
                            </>
                          ) : (
                            <Typography
                              sx={{ color: '#6B7280', fontSize: '0.875rem' }}
                            >
                              -
                            </Typography>
                          )}
                        </TableCell>
                      )}
                      {visibleColumns.floor && (
                        <TableCell
                          sx={{ color: '#6B7280', fontSize: '0.875rem' }}
                        >
                          {unit.floor || '-'}
                        </TableCell>
                      )}
                      {visibleColumns.condition && (
                        <TableCell
                          sx={{ color: '#6B7280', fontSize: '0.875rem' }}
                        >
                          {unit.condition || '-'}
                        </TableCell>
                      )}
                      {visibleColumns.amenities && (
                        <TableCell
                          sx={{ color: '#6B7280', fontSize: '0.875rem' }}
                        >
                          {unit.amenities && unit.amenities.length > 0
                            ? unit.amenities.join(', ')
                            : '-'}
                        </TableCell>
                      )}
                      {visibleColumns.currentRent && (
                        <TableCell>
                          {unit.currentRent > 0 ? (
                            <Typography
                              sx={{
                                fontWeight: 500,
                                color: '#111827',
                                fontSize: '0.875rem',
                              }}
                            >
                              ${unit.currentRent.toLocaleString()}
                            </Typography>
                          ) : (
                            <Typography
                              sx={{ color: '#6B7280', fontSize: '0.875rem' }}
                            >
                              -
                            </Typography>
                          )}
                        </TableCell>
                      )}
                      {visibleColumns.marketRent && (
                        <TableCell
                          sx={{
                            fontWeight: 500,
                            color: '#111827',
                            fontSize: '0.875rem',
                          }}
                        >
                          ${unit.marketRent.toLocaleString()}
                        </TableCell>
                      )}
                      {visibleColumns.delta && (
                        <TableCell>
                          <Typography
                            sx={{
                              color:
                                Math.max(0, unit.delta) > 0
                                  ? '#4FA490'
                                  : '#6B7280',
                              fontWeight:
                                Math.max(0, unit.delta) > 0 ? 500 : 400,
                              fontSize: '0.875rem',
                            }}
                          >
                            {Math.max(0, unit.delta) > 0
                              ? `+$${Math.max(0, unit.delta)}`
                              : '$0'}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleColumns.status && (
                        <TableCell>
                          <Chip
                            label={unit.status}
                            size='small'
                            sx={{
                              backgroundColor: statusColors.bg,
                              color: statusColors.text,
                              fontWeight: 600,
                              fontSize: '0.75rem',
                            }}
                          />
                        </TableCell>
                      )}
                      {visibleColumns.leaseEnd && (
                        <TableCell>
                          <Typography
                            sx={{ color: '#6B7280', fontSize: '0.875rem' }}
                          >
                            {unit.leaseEnd || '-'}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleColumns.lastUpdate && (
                        <TableCell
                          sx={{ color: '#6B7280', fontSize: '0.875rem' }}
                        >
                          {unit.lastUpdate}
                        </TableCell>
                      )}
                      {visibleColumns.notes && (
                        <TableCell
                          sx={{ color: '#6B7280', fontSize: '0.875rem' }}
                        >
                          {unit.notes || '-'}
                        </TableCell>
                      )}
                      <TableCell>
                        <IconButton
                          size='small'
                          onClick={() => handleOpenEditDialog(unit)}
                          sx={{ padding: '0.25rem' }}
                        >
                          <Edit3 size={16} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Box
        sx={{
          borderTop: '1px solid #E5E7EB',
          backgroundColor: '#FFFFFF',
          paddingX: { xs: '1rem', md: '1.5rem' },
          paddingY: '0.75rem',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: '0.5rem', sm: 0 },
          }}
        >
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: '#6B7280',
            }}
          >
            {total} units total
            {(searchQuery ||
              filterBuilding !== 'all' ||
              filterBed !== 'all' ||
              filterStatus !== 'all') &&
              ` (filtered)`}
          </Typography>
          {totalPages > 1 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  color: '#6B7280',
                }}
              >
                Page {page} of {totalPages}
              </Typography>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                size='small'
                shape='rounded'
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: '0.75rem',
                  },
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
      <EditUnitDialog
        open={Boolean(editingUnit)}
        onClose={handleDialogClose}
        unit={editingUnit}
        onSuccess={handleDialogSuccess}
      />
    </Box>
  );
};

export default UnitsGrid;
